import { useCallback, type ReactNode } from 'react';

import { getWorkspaceScope } from '@/app/workspace/store';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The platform's OPERATIONS for the tenant this console is bound to (VND-60).
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS SEPARATE FROM THE QUEUES
 * ---------------------------------------------------------------------------
 * One widget holds one read. The platform-wide queue depths are a read; the projection for one
 * affected tenant is another. Bundling them would mean a failure of the second took the first
 * down with it — and a platform administrator would lose sight of the queues because one
 * tenant's projection was refused.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE NUMBERS ARE
 * ---------------------------------------------------------------------------
 * M63 counts the interventions the platform has opened against this tenant, by class, and how
 * many are still awaiting authorisation or an outcome, plus the approvals that are open. The
 * classes are the API's own.
 */
const CLASS_LABEL = [
  ['vendor', 'Vendor'],
  ['moderation', 'Moderation'],
  ['finance', 'Finance'],
  ['incident', 'Incident'],
  ['access', 'Access'],
  ['release', 'Release'],
] as const;

/** The window the projection is asked for. The API states the window it answered with. */
const VENDOR_WINDOW_DAYS = 30;

function vendorWindow(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - VENDOR_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** The interventions the platform has opened against the tenant, by class. */
export type VendorOperationClassesProps = {
  readonly byClass: Readonly<Record<string, number>>;
};

function VendorOperationClasses({ byClass }: VendorOperationClassesProps): ReactNode {
  return (
    <ul className="flex flex-col">
      {CLASS_LABEL.map(([key, label]) => (
        <li
          key={key}
          className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
        >
          <span className="text-content">{label}</span>
          <span className="font-medium text-content">{byClass[key] ?? 0}</span>
        </li>
      ))}
    </ul>
  );
}

export function VendorOperations({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => {
    const tenant = getWorkspaceScope()?.tenant;
    if (tenant === undefined) {
      // No bound tenant means there is nothing to project. The widget renders its empty state
      // rather than asking the API about a tenant the console does not have.
      return Promise.reject(new Error('This console is not bound to a tenant.'));
    }
    const window = vendorWindow();
    return dashboardApi.vendorOperationsSummary(tenant.id, window.from, window.to);
  }, []);

  return (
    <DashboardWidgetFrame
      title="Operations for this tenant"
      load={load}
      className={className}
      isEmpty={(data) =>
        Object.values(data.adminActionsByClass).every((count) => count === 0)
      }
      emptyTitle="No interventions"
      emptyDescription="The platform has opened no interventions against this tenant."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label="Awaiting authorisation"
              value={String(data.pendingAuthorizationCount)}
              tone={data.pendingAuthorizationCount === 0 ? 'neutral' : 'warning'}
            />
            <StatCard
              label="Awaiting an outcome"
              value={String(data.outcomeUnconfirmedCount)}
              tone={data.outcomeUnconfirmedCount === 0 ? 'neutral' : 'warning'}
            />
            <StatCard
              label="Approvals open"
              value={String(data.openApprovalCaseCount)}
              tone={data.openApprovalCaseCount === 0 ? 'neutral' : 'warning'}
            />
          </div>

          <VendorOperationClasses byClass={data.adminActionsByClass} />

          <p className="text-xs text-content-muted">
            Window: {data.window.from} to {data.window.to}. Audit trail{' '}
            {data.auditTrailPresent ? 'present' : 'absent'} for this tenant.
          </p>
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
